import { Room, RoomEvent } from 'livekit-client';

class LiveKitService {
  constructor(store) {
    this.room = null;
    this.store = store; // Store methods
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectTimeout = null;

    if (!this.store) {
      console.warn('LiveKitService initialized without store. Some features may not work correctly.');
    }
  }

  async connectToRoom(url, token, options = {}) {
    if (this.room) {
      console.warn('Already connected to a room');
      return this.room;
    }

    this.isConnecting = true;
    if (this.store.setConnecting) this.store.setConnecting(true);
    if (this.store.setError) this.store.setError(null);

    try {
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          audio: true,
          video: false,
          screenShare: false,
        },
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        ...options,
      });

      this.setupEventListeners();

      await this.room.connect(url, token);

      this.isConnecting = false;
      this.store.setConnecting(false);
      this.store.setConnected(true);
      this.reconnectAttempts = 0;

      return this.room;
    } catch (error) {
      console.error('Failed to connect to room:', error);
      this.isConnecting = false;
      this.store.setConnecting(false);
      this.store.setError('No se pudo conectar a la sala. Intenta de nuevo.');
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.room) return;

    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('Participant connected:', participant.identity);
      this.store.addParticipant({
        id: participant.sid,
        identity: participant.identity,
        isLocal: false,
        audioTrack: null,
        isMuted: true,
      });
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('Participant disconnected:', participant.identity);
      this.store.removeParticipant(participant.sid);
    });

    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('Track subscribed:', track.kind, 'from', participant.identity);
      if (track.kind === 'audio') {
        this.attachAudioTrack(track, participant);
      }
    });

    this.room.on(RoomEvent.TrackUnpublished, (publication, participant) => {
      console.log('Track unpublished:', publication.kind, 'from', participant.identity);
      if (publication.kind === 'audio') {
        this.detachAudioTrack(participant);
      }
    });

    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('Connection state changed:', state);
      if (state === 'disconnected' || state === 'failed') {
        this.handleDisconnection();
      }
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      console.log('Reconnecting to room...');
      this.store.setError('Reconectando...');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      console.log('Reconnected to room');
      this.store.setError(null);
      this.reconnectAttempts = 0;
    });
  }

  attachAudioTrack(track, participant) {
    if (track.kind !== 'audio') return;

    const elementId = `audio-${participant.sid}`;
    let audioElement = document.getElementById(elementId);

    if (!audioElement) {
      audioElement = document.createElement('audio');
      audioElement.id = elementId;
      audioElement.autoplay = true;
      audioElement.volume = 1.0;
      document.body.appendChild(audioElement);
    }

    track.attach(audioElement);

    this.store.updateParticipant(participant.sid, {
      audioTrack: track,
      isMuted: false,
    });
  }

  detachAudioTrack(participant) {
    const elementId = `audio-${participant.sid}`;
    const audioElement = document.getElementById(elementId);

    if (audioElement) {
      audioElement.pause();
      audioElement.srcObject = null;
      audioElement.remove();
    }

    this.store.updateParticipant(participant.sid, {
      audioTrack: null,
      isMuted: true,
    });
  }

  handleDisconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.store.setError('Se perdió la conexión. Por favor, inténtalo de nuevo.');
      this.disconnect();
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 15000);

    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    this.store.setError(`Intentando reconectar (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      if (this.room) {
        this.room.reconnect();
      }
    }, delay);
  }

  async toggleMute(muted) {
    if (!this.room) return false;

    try {
      const localTrack = this.room.localParticipant.audioTrack;
      if (!localTrack) return false;

      if (muted) {
        await localTrack.mute();
      } else {
        await localTrack.unmute();
      }

      this.store.setMuted(muted);
      return muted;
    } catch (error) {
      console.error('Failed to toggle mute:', error);
      this.store.setError('No se pudo cambiar el estado del micrófono');
      return !muted;
    }
  }

  async disconnect() {
    if (!this.room) return;

    try {
      // Remover todos los event listeners de forma segura
      this.room.removeAllListeners();
      
      await this.room.disconnect();
    } catch (error) {
      console.error('Error disconnecting from room:', error);
    } finally {
      this.room = null;
      this.isConnecting = false;
      if (this.store && this.store.reset) {
        this.store.reset();
      }
      clearTimeout(this.reconnectTimeout);
    }
  }

  getCurrentRoom() {
    return this.room;
  }

  isConnected() {
    return this.room !== null;
  }
}

export default LiveKitService;
